import { useCallback } from 'react';
import { useParams } from 'react-router';
import { Button, Center, Row, Screen, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { CardSpinner, ConfirmPopup } from '~/commonComponents';
import browserHistory from '~/browserHistory';
import {
  fireBulkConfirmCandidateSelected,
  fireBulkConfirmConfirmClicked,
  fireBulkConfirmNotNowClicked,
} from '~/posthogs/events';

import { CircularLoading } from '~/images/lottie';
import { CandidateProfile } from '../candidates';
import { ShiftList } from './components';
import { useBulkConfirmation } from './hooks/useBulkConfirmation';
import { useCandidateJobs } from './hooks/useCandidateJobs';
import { useJobSelection } from './hooks/useJobSelection';
import { OutlineCheckCircleConfirmationIcon, OutlineWarning01Icon } from '@tempmee/react-icons';

export const BulkConfirmCandidateJobs = () => {
  const params = useParams<{ candidateId: string; officeId: string }>();

  const { candidate, jobs, isContractor, isEmployee, isFirstLoading } = useCandidateJobs(
    params.candidateId || '',
    params.officeId || '',
  );

  const {
    checkedJobs,
    clearCheckedJobs,
    hasSelection,
    totalCheckedJobs,
    handleToggleOption,
    isMultipleSelection,
    selectedJobIds,
  } = useJobSelection(jobs);

  const {
    confirmJobs,
    isLoadingJobsConfirmation,
    isJobsConfirmationSuccess,
    bulkConfirmationError,
    handleCloseErrorPopup,
    handleCloseSuccessPopup,
    confirmedJobIds,
  } = useBulkConfirmation({
    candidateId: params.candidateId || '',
  });

  const handleSelectJob = useCallback(
    (jobId?: string) => {
      if (jobId && !checkedJobs[jobId]) {
        fireBulkConfirmCandidateSelected({
          candidateId: params.candidateId,
          shiftId: jobId,
        });
      }
      handleToggleOption(jobId);
    },
    [checkedJobs, handleToggleOption, params.candidateId],
  );

  const handleConfirmJobs = useCallback(() => {
    fireBulkConfirmConfirmClicked({
      candidateId: params.candidateId,
      candidateCount: selectedJobIds.length,
    });
    confirmJobs(jobs, selectedJobIds);
    clearCheckedJobs();
  }, [confirmJobs, jobs, selectedJobIds, clearCheckedJobs, params.candidateId]);

  if (isFirstLoading || !candidate || !jobs.length) {
    return <CardSpinner />;
  }

  return (
    <>
      <Screen variant="standard">
        <Screen.Header title="Confirm Shifts" />
        <Screen.Body>
          <Row align="center" justify="between">
            <CandidateProfile
              candidate={candidate.candidate}
              jobId={jobs[0].id}
              job={jobs[0].jobApi}
              hasWorkedWithDSO={candidate.hasWorkedWithDSO}
              hasWorkedWithOffice={candidate.hasWorkedWithOffice}
              backgroundCheckStatus={candidate?.backgroundCheckStatus}
            />
            <Row gap={3}>
              <Button
                variant="secondary"
                size="md"
                disabled={isLoadingJobsConfirmation}
                onClick={() => {
                  fireBulkConfirmNotNowClicked({ candidateId: params.candidateId });
                  browserHistory.push('/');
                }}
              >
                Not Now
              </Button>
              <Button
                disabled={!hasSelection || isLoadingJobsConfirmation || jobs.length === 0}
                size="md"
                onClick={handleConfirmJobs}
              >
                {totalCheckedJobs > 0 && isContractor ? `Confirm (${totalCheckedJobs})` : 'Confirm'}
              </Button>
            </Row>
          </Row>
          <Center px={20} style={{ marginBlock: 32 }}>
            <Typography variant="body1"
              italic
              color={colors.neutral[500]}
              style={{ textAlign: 'center' }}
            >
              The list below displays additional offers that {candidate.candidate.first_name}{' '}
              {candidate.candidate.last_name} has submitted for other shifts at your office. Review
              the candidate&apos;s application details, including any counters they submitted for
              the hourly rate and/or shift hours.
            </Typography>
          </Center>
          <ShiftList
            candidate={candidate.candidate}
            checkedJobs={checkedJobs}
            isMultipleSelection={isMultipleSelection}
            jobs={jobs}
            handleToggleOption={handleSelectJob}
          />
        </Screen.Body>
      </Screen>
      {isLoadingJobsConfirmation && (
        <ConfirmPopup
          Icon={() => (
            <Center>
              <CircularLoading />
            </Center>
          )}
          title="Processing..."
          titleStyle={{ marginTop: 36, marginBottom: 8 }}
          description="Please, wait while we confirm your selected shifts. Thank you for your patience."
          descriptionStyle={{ color: colors.neutral[500], marginBottom: 0 }}
          modalStyle={{
            paddingTop: 64,
            paddingBottom: 64,
          }}
        />
      )}
      {isJobsConfirmationSuccess && (
        <ConfirmPopup
          Icon={() => (
            <Center>
              <OutlineCheckCircleConfirmationIcon color={colors.success[400]} />
            </Center>
          )}
          title="Good News!"
          titleStyle={{ marginTop: 28, marginBottom: 8 }}
          description={
            isEmployee
              ? 'Your shift has successfully been filled.'
              : // eslint-disable-next-line max-len
                `${candidate.candidate.first_name} ${candidate.candidate.last_name} has been confirmed for ${confirmedJobIds.length} shift(s).`
          }
          descriptionStyle={{ color: colors.neutral[500], marginBottom: 40 }}
          modalStyle={{
            paddingTop: 64,
            paddingBottom: 64,
          }}
          rightButtonText="Ok"
          rightButtonAction={handleCloseSuccessPopup}
        />
      )}

      {bulkConfirmationError && (
        <ConfirmPopup
          title="Sorry!"
          titleStyle={{ marginTop: 28, marginBottom: 8 }}
          Icon={() => <OutlineWarning01Icon color={colors.warning[500]} />}
          description={
            bulkConfirmationError.message ||
            'Unable to confirm any of the selected shifts. Please try again later.'
          }
          descriptionStyle={{ color: colors.neutral[500], marginBottom: 40 }}
          modalStyle={{
            paddingTop: 64,
            paddingBottom: 64,
          }}
          rightButtonText="Close"
          rightButtonAction={handleCloseErrorPopup}
        />
      )}
    </>
  );
};
